/**
 * Auth shell (T017). Google Workspace SSO restricted to `cosmic-chimps.com`, session observer that
 * surfaces the role custom claim, and a domain guard for the route guard in App.tsx.
 */
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type Unsubscribe,
} from 'firebase/auth';
import { auth } from './firebase';
import type { Role } from '../types/entities';

export const WORKSPACE_DOMAIN = 'cosmic-chimps.com';

export interface SessionUser {
  uid: string;
  email: string;
  /** Role custom claim; null = read-only (FR-019). */
  role: Role | null;
  /** True only when the verified `hd` claim is the Workspace domain. */
  isDomainUser: boolean;
}

/** Start Google Workspace SSO, hinting the Cosmic Chimps domain. */
export async function signIn(): Promise<void> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ hd: WORKSPACE_DOMAIN, prompt: 'select_account' });
  await signInWithPopup(auth, provider);
}

export async function logout(): Promise<void> {
  await signOut(auth);
}

/** Observe the session; the callback receives the domain user (with role claim) or null. */
export function onSession(cb: (user: SessionUser | null) => void): Unsubscribe {
  return onAuthStateChanged(auth, async (user) => {
    if (!user) return cb(null);
    const token = await user.getIdTokenResult();
    const hd = token.claims.hd as string | undefined;
    const email = user.email ?? '';
    cb({
      uid: user.uid,
      email,
      role: (token.claims.role as Role | undefined) ?? null,
      // Domain is established from the VERIFIED email (the load-bearing signal, mirroring lib/rbac.ts);
      // Google's `hd` claim is accepted as a best-effort extra IF a sign-in blocking function ever sets
      // it (none exists today). Exact `@domain` suffix rejects look-alikes. Safe while Google is the
      // only sign-in provider (see rbac.ts invariant).
      isDomainUser: (user.emailVerified && email.endsWith(`@${WORKSPACE_DOMAIN}`)) || hd === WORKSPACE_DOMAIN,
    });
  });
}
